import React, { useState, useEffect } from 'react';
import {
  FileText, Mail, MessageSquare, ArrowUpRight, ArrowDownLeft,
  Clock, Download, RefreshCw, Smartphone, ExternalLink,
  AlertCircle, CheckCircle, Send, Upload, Edit3, GitBranch
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';

const ACTIVITY_CONFIG = {
  doc_generated: { icon: FileText, color: 'bg-blue-100 text-blue-600', label: 'Document Generated' },
  email_sent: { icon: Mail, color: 'bg-green-100 text-green-600', label: 'Email Sent' },
  email_received: { icon: Mail, color: 'bg-cyan-100 text-cyan-600', label: 'Email Received' },
  sms_sent: { icon: Smartphone, color: 'bg-emerald-100 text-emerald-600', label: 'SMS Sent' },
  sms_received: { icon: Smartphone, color: 'bg-teal-100 text-teal-600', label: 'SMS Received' },
  portal_message: { icon: MessageSquare, color: 'bg-purple-100 text-purple-600', label: 'Portal Message' },
  stage_change: { icon: GitBranch, color: 'bg-orange-100 text-orange-600', label: 'Stage Changed' },
  note_added: { icon: Edit3, color: 'bg-yellow-100 text-yellow-600', label: 'Note Added' },
  doc_uploaded: { icon: Upload, color: 'bg-indigo-100 text-indigo-600', label: 'Document Uploaded' },
  doc_downloaded: { icon: Download, color: 'bg-gray-100 text-gray-600', label: 'Document Downloaded' },
  internal_chat: { icon: MessageSquare, color: 'bg-violet-100 text-violet-600', label: 'Internal Chat' },
};

export default function ActivityTimeline({ recordId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTimeline(); }, [recordId]);

  const loadTimeline = async () => {
    try {
      const { data } = await client.get(`/communication-log/${recordId}`, { params: { limit: 5 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const handleDownload = async (item) => {
    if (!item.file_id) return;
    try {
      const { data } = await client.get(`/documents/${item.file_id}/download`);
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 text-sm">Activity & Communications</h3>
          {total > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{total}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={loadTimeline} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div>
        {loading ? (
          <div className="text-center py-6 text-gray-400 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-6">
            <Clock className="w-6 h-6 text-gray-200 mx-auto mb-1" />
            <p className="text-gray-400 text-xs">No activity yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((item) => {
              const config = ACTIVITY_CONFIG[item.activity_type] || { icon: Clock, color: 'bg-gray-100 text-gray-500', label: item.activity_type };
              const Icon = config.icon;
              return (
                <div key={item.id} className="flex gap-3 px-4 py-2.5 hover:bg-gray-50 transition">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-900">{config.label}</span>
                      <span className="text-[10px] text-gray-400">{formatTime(item.created_at)}</span>
                    </div>
                    {item.subject && <p className="text-xs text-gray-500 truncate mt-0.5">{item.subject}</p>}
                    {item.to_address && <p className="text-[10px] text-gray-400 mt-0.5">To: {item.to_address}</p>}
                    {item.file_name && (
                      <button onClick={() => handleDownload(item)}
                        className="flex items-center gap-1 mt-1 text-[10px] text-blue-600 hover:text-blue-700">
                        <FileText className="w-3 h-3" />{item.file_name}
                        <Download className="w-2.5 h-2.5" />
                      </button>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {item.performer_first_name ? item.performer_first_name + ' ' + (item.performer_last_name || '') : 'System'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > 5 && (
          <button
            onClick={() => navigate(`activity`)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 border-t border-gray-100 transition">
            View All Activity ({total})
            <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
